// src/components/admin/SettingsManagement.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const API_BASE = process.env.REACT_APP_API_URL;

if (!API_BASE) {
  console.error('❌ CRITICAL: REACT_APP_API_URL not configured in SettingsManagement!');
}

const SettingsManagement = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('organization');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      const response = await fetch(`${API_BASE}/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      console.log('Settings API response:', data);

      if (data.success) {
        // The response format is { success: true, data: { settings } }
        const settingsData = data.data?.settings || {};

        // If it's an array, convert to grouped format
        if (Array.isArray(settingsData)) {
          const grouped = {};
          settingsData.forEach(setting => {
            const category = setting.category || 'general';
            if (!grouped[category]) {
              grouped[category] = {};
            }
            grouped[category][setting.setting_key] = {
              value: setting.setting_value,
              type: setting.setting_type,
              description: setting.description
            };
          });
          setSettings(grouped);
        } else {
          // Already grouped
          setSettings(settingsData);
        }
      } else {
        toast.error(data.message || 'Failed to fetch settings');
        setSettings({}); // Set empty object instead of leaving undefined
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      toast.error('Failed to fetch settings');
      setSettings({}); // Set empty object on error
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: {
          ...prev[category]?.[key],
          value: value
        }
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('adminToken');

      // Convert settings object to array format
      const settingsArray = [];
      Object.keys(settings).forEach(category => {
        Object.keys(settings[category]).forEach(key => {
          settingsArray.push({
            category,
            key,
            value: settings[category][key].value,
            type: settings[category][key].type,
            description: settings[category][key].description
          });
        });
      });

      const response = await fetch(`${API_BASE}/admin/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ settings: settingsArray })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Settings saved successfully');
        setSettings(data.data || {});
      } else {
        toast.error(data.message || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const renderInputField = (category, key, setting) => {
    const value = setting?.value || '';
    const type = setting?.type || 'string';

    if (type === 'boolean') {
      return (
        <select
          className="form-select bg-white text-dark border-2"
          value={value}
          onChange={(e) => handleInputChange(category, key, e.target.value)}
        >
          <option value="true">Enabled</option>
          <option value="false">Disabled</option>
        </select>
      );
    }

    if (type === 'text') {
      return (
        <textarea
          className="form-control bg-white text-dark border-2"
          value={value}
          onChange={(e) => handleInputChange(category, key, e.target.value)}
          rows="3"
        />
      );
    }

    if (type === 'password') {
      return (
        <input
          type="password"
          className="form-control bg-white text-dark border-2"
          value={value}
          onChange={(e) => handleInputChange(category, key, e.target.value)}
          placeholder="••••••••"
        />
      );
    }

    return (
      <input
        type={type === 'number' ? 'number' : 'text'}
        className="form-control bg-white text-dark border-2"
        value={value}
        onChange={(e) => handleInputChange(category, key, e.target.value)}
      />
    );
  };

  const renderCategorySettings = (category) => {
    const categorySettings = settings[category] || {};
    const categoryTitle = category.charAt(0).toUpperCase() + category.slice(1);

    // If membership category, also include pricing settings
    let allSettings = { ...categorySettings };
    if (category === 'membership' && settings.pricing) {
      allSettings = { ...categorySettings, ...settings.pricing };
    }

    // Define custom order for membership pricing fields
    const membershipOrder = [
      'default_membership_duration',
      'registration_enabled',
      'membership_fee_professional',
      'membership_fee_student',
      'international_price',
      'associate_price',
      'professional_price',
      'student_price',
      'vendor_bronze_price',
      'vendor_silver_price',
      'vendor_gold_price',
      'vendor_platinum_price',
      'vendor_diamond_price'
    ];

    // Sort keys based on membership order if in membership category
    const sortedKeys = category === 'membership'
      ? Object.keys(allSettings).sort((a, b) => {
        const aIndex = membershipOrder.indexOf(a);
        const bIndex = membershipOrder.indexOf(b);
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      })
      : Object.keys(allSettings);

    return (
      <div className="card border-0 shadow-sm">
        <div className="card-header" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B2F 100%)', color: 'white', border: 'none' }}>
          <h5 className="mb-0 fw-bold">
            <i className="bi bi-gear me-2"></i>
            {categoryTitle} Settings
          </h5>
        </div>
        <div className="card-body bg-white p-4">
          {sortedKeys.length === 0 ? (
            <p className="text-muted">No settings available for this category</p>
          ) : (
            <div className="row g-3">
              {sortedKeys.map(key => {
                const setting = allSettings[key];
                const actualCategory = settings.pricing && settings.pricing[key] ? 'pricing' : category;
                return (
                  <div className="col-md-6" key={key}>
                    <label className="form-label fw-bold text-dark mb-2">
                      {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </label>
                    {renderInputField(actualCategory, key, setting)}
                    {setting?.description && (
                      <small className="text-muted d-block mt-1">{setting.description}</small>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border" style={{ color: '#6B8E23' }} role="status"></div>
        <span className="ms-3 text-muted">Loading settings...</span>
      </div>
    );
  }

  const categories = Object.keys(settings);

  return (
    <div className="container-fluid" style={{ padding: '20px 15px' }}>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
        <div>
          <h2 className="h3 fw-bold mb-1" style={{ color: '#6B8E23' }}>System Settings</h2>
          <p className="text-muted mb-0 small">Configure system-wide settings and preferences</p>
        </div>
        <button
          onClick={handleSave}
          className="btn fw-bold shadow-sm"
          style={{ backgroundColor: '#6B8E23', color: 'white', border: 'none', whiteSpace: 'nowrap' }}
          disabled={saving}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#556B2F'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#6B8E23'}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Saving...
            </>
          ) : (
            <>
              <i className="bi bi-check-circle me-2"></i>Save Changes
            </>
          )}
        </button>
      </div>

      {/* Category Selector */}
      <div className="mb-4">
        <label className="form-label fw-bold" style={{ color: '#6B8E23' }}>Select Settings Category</label>
        <select
          className="form-select form-select-lg"
          value={activeTab}
          onChange={(e) => setActiveTab(e.target.value)}
          style={{
            borderColor: '#6B8E23',
            fontSize: '1rem',
            padding: '12px 16px'
          }}
        >
          {categories.map(category => (
            <option key={category} value={category}>
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Settings Content */}
      <div className="tab-content">
        {renderCategorySettings(activeTab)}
      </div>

      {/* Info Box */}
      <div className="alert mt-4 border-0 shadow-sm" style={{ backgroundColor: '#f0f7e8', borderLeft: '4px solid #6B8E23' }}>
        <i className="bi bi-info-circle me-2" style={{ color: '#6B8E23' }}></i>
        <strong style={{ color: '#6B8E23' }}>Note:</strong> Changes to system settings will take effect immediately after saving.
        Some settings may require a server restart to fully apply.
      </div>
    </div>
  );
};

export default SettingsManagement;
